import { useState } from 'react'
import { motion } from 'framer-motion'
import { useReducedMotion } from '@/lib/useReducedMotion'
import { career } from '@/content/portfolio'

export function Experience() {
  const [expanded, setExpanded] = useState<number[]>([])
  const reducedMotion = useReducedMotion()
  return (
    <section id="experience" className="scroll-mt-24 border-t border-vibe-border bg-vibe-black py-16 lg:py-24">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <div>
          <div className="mb-8 flex items-end justify-between lg:mb-12">
            <div>
              <div className="section-label mb-2">02 / My path</div>
              <h2 className="text-3xl lg:text-4xl font-medium tracking-tight text-text-heading">
                From engineer to product builder.
              </h2>
            </div>
            <div className="hidden font-mono text-sm text-text-muted lg:block">2017 — 2026</div>
          </div>
        </div>

        <div className="divide-y divide-vibe-border border-y border-vibe-border">
          {career.map((role, index) => (
              <motion.article key={`${role.company}-${role.title}`} initial={reducedMotion ? false : 'hidden'} animate={reducedMotion ? 'visible' : undefined} whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="relative py-6 pl-8 lg:py-8 lg:pl-10">
                <div aria-hidden="true" className="absolute bottom-0 left-1.5 top-0 w-px bg-vibe-border-strong">
                  <motion.div className="h-full origin-top bg-accent-primary/60" variants={{ hidden: { scaleY: 0 }, visible: { scaleY: 1 } }} transition={{ duration: reducedMotion ? 0 : 0.65, ease: 'easeOut' }} />
                </div>
                <motion.span aria-hidden="true" className="absolute left-0 top-8 h-3 w-3 rounded-full border-2 border-accent-primary bg-vibe-black lg:top-10" variants={{ hidden: { opacity: 0, scale: 0.6 }, visible: { opacity: 1, scale: 1 } }} transition={{ duration: reducedMotion ? 0 : 0.3, delay: reducedMotion ? 0 : 0.12 }} />
                <div className="flex flex-col gap-1 lg:flex-row lg:items-baseline lg:justify-between lg:gap-4">
                  <div>
                    <h3 className="text-lg lg:text-xl font-medium tracking-tight text-text-heading">{role.title}</h3>
                    <div className="font-medium text-accent-primary">{role.company}</div>
                  </div>
                  <div className="shrink-0 font-mono text-sm text-text-muted">{role.period}</div>
                </div>
                <p className="mt-4 text-[15px] font-medium leading-relaxed text-text-primary">{role.focus}</p>
                <button type="button" aria-expanded={expanded.includes(index)} aria-controls={`career-detail-${index}`} onClick={() => setExpanded(current => current.includes(index) ? current.filter(item => item !== index) : [...current, index])} className="mt-4 py-2 text-sm text-accent-primary hover:text-text-primary">
                  {expanded.includes(index) ? '− Hide details' : '+ Explore role'}<span className="sr-only">: {role.title} at {role.company}</span>
                </button>
                <motion.div id={`career-detail-${index}`} aria-hidden={!expanded.includes(index)} initial={false} animate={{ height: expanded.includes(index) ? 'auto' : 0, opacity: expanded.includes(index) ? 1 : 0 }} transition={{ duration: reducedMotion ? 0 : 0.22 }} className="overflow-hidden">
                <ul className="mt-4 space-y-2.5 text-[15px] leading-relaxed text-text-secondary">
                  {role.highlights.map((highlight) => (
                    <li key={highlight} className="flex gap-3">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent-primary/70" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
                {(role.skills || role.techStack) && (
                  <div className="mt-5 border-t border-vibe-border pt-4">
                    <div className="section-label mb-2">{role.skills ? 'Product skills' : 'Tech stack'}</div>
                    <p className="text-sm leading-relaxed text-text-secondary">{(role.skills || role.techStack)?.join(' · ')}</p>
                  </div>
                )}
                </motion.div>
              </motion.article>
          ))}
        </div>
        <p className="mt-8 font-mono text-sm leading-relaxed text-text-secondary">Bachelor of Information Technology · RMIT University Vietnam · 2013–2017</p>
      </div>
    </section>
  )
}
